import { z } from 'zod';
import type { FilterCondition, FilterFieldType, FilterGroup } from './filter';
import type { FlowSort, FlowView } from './view';
import { ContactRoleSchema } from '../product-usage/wire';

/**
 * The ONE rep-facing name for the Product Usage role column and its filter
 * group ("Clay Role"). Read by the `usage_roles` catalog entry below, by
 * `USAGE_ROLE_FILTER_FIELDS` (filter-fields.ts) as the picker section, and by
 * the tests, so renaming it is a one-string change.
 */
export const USAGE_ROLE_LABEL = 'Clay Role';

/** Catalog key of the Clay Role column, the row field `toWireRow` folds the three role flags into. */
export const USAGE_ROLE_KEY = 'usage_roles';

/**
 * Contact view wire contract: the field catalog (labels/types for columns +
 * the filter builder), the row schema the /api/flow/contacts endpoint
 * returns, and the seeded v0 presets (code-defined, createdBy = null).
 *
 * Field keys here must match the SQL aliases in the endpoint's field map
 * (src/api/flow.ts) — the endpoint's contract test asserts that.
 */

// ── Field catalog ────────────────────────────────────────────────────────────

export interface ContactFieldDef {
  key: string;
  label: string;
  type: FilterFieldType;
  /** Not offered in the filter builder (e.g. computed links). */
  noFilter?: boolean;
  /**
   * Offered in the Group picker even though it is a number column (number columns are
   * otherwise excluded: grouping on a continuous value makes one header per row). For
   * low-cardinality ordinals such as the F500 rank.
   */
  groupable?: boolean;
}

/**
 * The `terra_signals` view's rep-facing fields on the CONTACT catalog (plan
 * 2026-09-10-001 D3/D13) — declared ONCE here and spread into `CONTACT_FIELDS`
 * below, so `CONTACT_SIGNAL_FIELD_KEYS` (exported for
 * `plans/2026-09-13-002-terra-signals-perf-robust-plan.md` B1 U2) is derived
 * from the same single list rather than hand-listed a second time anywhere
 * else. Every consumer that needs to know "is this a Signal column" —
 * `/api/flow/contacts`' conditional `sig` join/select and
 * `ContactFlowTable.tsx`'s `include=signals` request param — reads this
 * export; neither hand-lists the nine keys itself.
 *
 * See `CONTACT_FIELDS`' own Signal-columns doc comment (just below, at the
 * splice point) for what each field means and which row it describes.
 */
const CONTACT_SIGNAL_FIELDS: ContactFieldDef[] = [
  { key: 'signal_status',        label: 'Signal Status',        type: 'picklist' },
  { key: 'has_responded',        label: 'Has Responded',        type: 'boolean' },
  { key: 'signal_subtype',       label: 'Signal Subtype',       type: 'picklist' },
  { key: 'signal_name',          label: 'Signal Name',          type: 'text' },
  { key: 'signal_type',          label: 'Signal Type',          type: 'picklist' },
  { key: 'signal_status_rank',   label: 'Signal Status Order',  type: 'number' },
  { key: 'signal_action_state',  label: 'Signal Action State',  type: 'picklist' },
  { key: 'signal_source_system', label: 'Signal Source System', type: 'picklist' },
  // A9 — `launcher_event_invitees.invitee_type` (`Prospect`, `Customer`, …),
  // free text, Campaign signals only. Default-visible on the Plays view via
  // the `contact-plays` preset below.
  { key: 'signal_invitee_type',  label: 'Invitee Type',         type: 'text' },
  // "Actioned By" (2026-09-22 plan) — who personally completed the action
  // (invited, sent) on this signal, `{me}`-filterable (person type, backed
  // by `action_completed_by_id`/`action_completed_by_name` on the view).
  // Front-loaded onto the `contact-plays` preset right after
  // `signal_invitee_type` (product decision F7).
  { key: 'signal_completed_by',  label: 'Actioned By',          type: 'person' },
];

/**
 * The ten Signal-column keys (`CONTACT_SIGNAL_FIELDS` above) — what
 * `/api/flow/contacts` (`src/api/flow.ts`) reads to decide whether the
 * request actually needs the `sig` display join (a signal filter/sort/
 * group_by, or `include=signals`), and what `ContactFlowTable.tsx` reads to
 * decide whether to SEND `include=signals` (any of these keys is a visible
 * column). `campaign_playhouse_visible` is deliberately NOT one of these:
 * it is `filterOnly` on both catalogs (no SELECT alias, no display column),
 * so it can never be a "visible column" and never needs to gate the display
 * join — filtering by it alone still reaches `terra_signals` through the
 * filter's own EXISTS, which is unconditional and unaffected by this list.
 */
export const CONTACT_SIGNAL_FIELD_KEYS: readonly string[] = CONTACT_SIGNAL_FIELDS.map(f => f.key);

/**
 * Play membership columns (plan 2026-09-24-005 U13 / D4; extended by plan
 * 2026-09-25-002 U1/D5) — the SELECTED play's WHOLE membership row for a
 * contact (every section now, not only "Not in this play yet"), off the
 * contacts route's `pm` derived join. They only have values on a request
 * carrying `play_id`/`play_type`/`play_section`; the route 400s "Play
 * columns need a selected play" for a filter/sort/group_by on any of them
 * without one. Declared once and spread into `CONTACT_FIELDS`, same pattern
 * as the Signal fields above.
 */
const CONTACT_PLAY_FIELDS: ContactFieldDef[] = [
  { key: 'play_suggested',            label: 'Suggested for play', type: 'boolean' },
  { key: 'play_status',               label: 'Play status',        type: 'picklist' },
  { key: 'play_invitee_type',         label: 'Invitee type',       type: 'picklist' },
  { key: 'play_has_responded',        label: 'Has responded',      type: 'boolean' },
  { key: 'play_last_queue_send_at',   label: 'Last send date',     type: 'date' },
  { key: 'play_last_queue_sent_by',   label: 'Sent by',            type: 'text' },
  { key: 'play_completed_by',         label: 'Actioned by',        type: 'person' },
];

/** The three play-column keys — the route's 400 pre-check and the client's `showPlayColumns` gate both read this. */
export const CONTACT_PLAY_FIELD_KEYS: readonly string[] = CONTACT_PLAY_FIELDS.map(f => f.key);

export const CONTACT_FIELDS: ContactFieldDef[] = [
  { key: 'name',                    label: 'Name',                 type: 'text' },
  { key: 'title',                   label: 'Title',                type: 'text' },
  { key: 'account_name',            label: 'Account Name',         type: 'text' },
  { key: 'fit_score',               label: 'Fit Score',            type: 'number' },
  { key: 'icp_fit_score',           label: 'ICP Fit Score',        type: 'number' },
  { key: 'persona',                 label: 'Persona',              type: 'text' },
  { key: 'persona_tier',            label: 'Persona Tier',         type: 'picklist' },
  { key: 'contact_stage',           label: 'Contact Stage',        type: 'picklist' },
  { key: 'total_engagement_score',  label: 'Total Engagement',     type: 'number' },
  { key: 'product_engagement_score',label: 'Product Engagement',   type: 'number' },
  { key: 'signals_engagement_score',label: 'Signal Engagement',    type: 'number' },
  { key: 'last_workspace_login',    label: 'Last WS Login',        type: 'date' },
  { key: 'email',                   label: 'Email',                type: 'text' },
  { key: 'last_email_sent',         label: 'Last Email Sent',      type: 'date' },
  { key: 'last_email_date',         label: 'Last Email (any)',     type: 'date' },
  { key: 'last_inbound_form',       label: 'Last Inbound Form',    type: 'date' },
  { key: 'last_activity',           label: 'Last Activity',        type: 'date' },
  { key: 'has_workspace',           label: 'Has Workspace',        type: 'boolean' },
  { key: 'gong_active_flows',       label: '# Active Flows',       type: 'number' },
  { key: 'no_longer_at_company',    label: 'No Longer at Co.',     type: 'boolean' },
  { key: 'has_opted_out_of_email',  label: 'Opted Out',            type: 'boolean' },
  { key: 'do_not_call',             label: 'Do Not Call',          type: 'boolean' },
  { key: 'suppression_reasons',     label: 'Suppression Reasons',  type: 'text' },
  { key: 'linkedin',                label: 'LinkedIn',             type: 'text', noFilter: true },
  { key: 'follows_clay_linkedin',   label: 'Follows Clay on LinkedIn', type: 'boolean' },
  { key: 'sfdc_link',               label: 'SFDC',                 type: 'text', noFilter: true },
  { key: 'gong_in_flow',            label: '⚡ In Flow',            type: 'boolean' },
  { key: 'gong_flow_name',          label: 'Flow Name',            type: 'text' },
  { key: 'lim_combined',            label: 'Last Interesting Moment', type: 'text' },
  { key: 'lim_date',                label: 'LIM Date',             type: 'date' },
  { key: 'lim_type',                label: 'LIM Type',             type: 'picklist' },
  { key: 'lim_name',                label: 'LIM Name',             type: 'text' },
  { key: 'lim_subtype',             label: 'LIM Subtype',          type: 'picklist' },
  { key: 'lim_description',         label: 'LIM Description',      type: 'text' },
  { key: 'lim_link',                label: 'LIM Link',             type: 'text', noFilter: true },
  { key: 'job_start_date',          label: 'Job Start Date',       type: 'date' },
  { key: 'job_level',               label: 'Job Level',            type: 'picklist' },
  { key: 'best_phone',              label: 'Best Phone',           type: 'text' },
  { key: 'owner_name',              label: 'Owner',                type: 'text' },
  { key: 'claydr_name',             label: 'ClayDR',               type: 'text' },
  { key: 'gs_name',                 label: 'Growth Strategist',    type: 'text' },
  { key: 'mailing_state',           label: 'Mailing State',        type: 'text' },
  { key: 'mailing_city',            label: 'Mailing City',         type: 'text' },
  { key: 'mailing_country',         label: 'Mailing Country',      type: 'text' },
  { key: 'created_date',            label: 'Created Date',         type: 'date' },
  { key: 'last_gtme_email',         label: 'Last GTME Email',      type: 'date' },
  { key: 'last_gs_meeting',         label: 'Last GS Meeting',      type: 'date' },
  { key: 'last_claydr_meeting',     label: 'Last CDR Meeting',     type: 'date' },
  { key: 'last_claydr_email',       label: 'Last CDR Email',       type: 'date' },
  { key: 'last_gtme_meeting',       label: 'Last GTME Meeting',    type: 'date' },
  // ── Account columns on a contact view (U5) ────────────────────────────────
  // The `account_*` cross-join fields (the `sa` LEFT JOIN already in the
  // contacts query) promoted from filter-only to real columns: SELECT-aliased
  // into ContactRow, so they show, sort and group like any other column.
  // Labels carry the "Account " prefix on purpose — in the column picker they
  // sit next to the contact's own fields, and a bare "ARR" reads as the
  // contact's. The picker's FILTER section keeps the short labels under its
  // own 'Account' group (CONTACT_ACCOUNT_CROSS_JOIN_FIELDS in
  // src/shared/flow/filter-fields.ts, which also skips these keys when
  // deriving filter entries from this catalog so each lands in the picker
  // exactly once).
  { key: 'account_arr',                       label: 'Account ARR',               type: 'number' },
  { key: 'account_open_pipeline',             label: 'Account Open Pipeline',     type: 'number' },
  { key: 'account_last_opportunity_stage',    label: 'Account Pipeline Stage',    type: 'picklist' },
  { key: 'account_number_of_open_opp',        label: 'Account # Open Opps',       type: 'number' },
  { key: 'account_propensity_score',          label: 'Account Propensity',        type: 'number' },
  { key: 'account_industry',                  label: 'Account Industry',          type: 'text' },
  { key: 'account_company_segment',           label: 'Account Segment',           type: 'picklist' },
  { key: 'account_sales_tier',                label: 'Account Sales Tier',        type: 'picklist' },
  { key: 'account_engagement_status_90_days', label: 'Account Engagement Status', type: 'picklist' },
  { key: 'account_number_of_employees',       label: 'Account Employees',         type: 'number' },
  { key: 'account_is_agency',                 label: 'Account Is Agency',         type: 'boolean' },
  { key: 'account_billing_country',           label: 'Account Country',           type: 'text' },
  { key: 'account_billing_city',              label: 'Account City',              type: 'text' },
  // Bucket A/E follow-up (U5, 2026-08-27): populates on the first hourly
  // object-sync after deploy (Region__c ships from Middleman as of
  // terra-middleware PR #257).
  { key: 'account_region',                    label: 'Account Region',            type: 'text' },
  { key: 'account_f500_rank',                 label: 'Account F500 Rank',         type: 'number', groupable: true },
  { key: 'account_owner_name',                label: 'Account Owner',             type: 'text' },
  { key: 'account_claydr_name',               label: 'Account ClayDR',            type: 'text' },
  // Tracker (U7 / R6) — Terra-only per-record state joined from
  // flow_record_trackers. Catalogued here so they can be SHOWN as columns and
  // grouped on; their filter-picker section is 'Tracker', declared once in
  // src/shared/flow/filter-fields.ts (TRACKER_FILTER_FIELDS) rather than
  // derived from this list. Off the default column set on purpose: the row
  // chip carries the status, and a rep who wants the columns adds them.
  //
  // `tracker` and `tracker_status` are deliberately NOT listed as columns
  // (U7 tracker polish): the row chip (FlowTable's `config.tracker` column,
  // header now labeled "Status") already shows the bucket, so a `tracker`
  // TEXT column duplicated it, and `tracker_status` is the raw enum a rep
  // never needs to see as a column. Both keys stay valid for `groupBy`
  // (TRACKER_FILTER_FIELDS still has `tracker`) and, for `tracker_status`,
  // for a pre-existing saved view's filter (CONTACT_FIELD_MAP keeps the SQL
  // mapping) — this removal is columns-only. A saved view whose `columns`
  // array still names one of these two keys degrades gracefully: FlowTable
  // filters unknown column keys out of `cols` (`Boolean` filter) and
  // ColumnsPopover otherwise preserves unknown keys untouched.
  { key: 'tracker_date',            label: 'Tracker Date',         type: 'date' },
  { key: 'tracker_note',            label: 'Tracker Note',         type: 'text' },
  // ── Job Changers / New Hires / Clay Users (U7, R1/R2) ─────────────────────
  // Direct `salesforce_contact` columns (U2's job-changer trio + Clay Users
  // field-add, Middleman PR #254) — unprefixed keys, same convention as every
  // other own-column contact field above (job_start_date, job_level, etc.);
  // the `account_`/`contact_` prefixes elsewhere in this codebase are
  // reserved for cross-entity join fields, which these are not (except
  // `came_from`, whose VALUE is a joined column but whose semantics are still
  // "a fact about this contact", so it keeps the unprefixed convention too).
  //
  // `came_from` is the previous account's NAME (LEFT JOIN salesforce_account
  // prev_sa ON prev_sa.salesforce_account_id = sc.job_change_previous_account_id,
  // src/api/flow.ts) — a real, filterable/sortable text column, powering the
  // "Former Job Changers" / "New Hires" presets' extra column. Its linking
  // companion (`came_from_account_id`, the raw previous-account SFDC id) rides
  // the wire on ContactRowSchema only — same pattern as
  // `account_lookalike_N_id` on AccountRowSchema (a wire-only field for the
  // frontend to build a link, not itself a filterable/sortable/showable
  // column) — so it is deliberately absent from this catalog.
  //
  // `past_employers_clay_customers` (text): `is_not_empty` is the "came from a
  // paying customer" filter (R1) — not a preset, an ad-hoc filter condition a
  // rep adds via the Filters popover. Deferred to a follow-up batch (R11); see
  // the plan's "Deferred to Follow-Up Work" section. Unlike this field,
  // `past_product_user` below DOES have a dedicated preset (U9, R11) —
  // `contact-past-product-user` in BASE_PRESETS.
  //
  // Empty-string emails (Risk, U2's schemas.ts comment): PR #254 widened the
  // Contact sync's WHERE clause to admit `Previous_Job_Changer__c = true` rows
  // regardless of email, so many of these contacts carry `email = ''` (empty
  // string, not SQL NULL). No special handling is needed HERE — `email`'s
  // FIELD_MAP type is already 'text', and condition-to-sql's `is_empty`/
  // `is_not_empty` for 'text' already test `(col IS NULL OR col = '')`, never
  // a bare `IS NULL` — so these rows already surface correctly in every
  // preset/filter. This note exists so nobody "fixes" that predicate to a
  // bare `IS NULL` later.
  // Raw value: `Product User`, `Job Change`, or one of 8 Opportunity-champion
  // outcomes (SFDC help text: "the type of engagement we had with this
  // contact in their previous role") — NOT a hire-recency signal, kept
  // neutral-labeled since most values aren't about product usage at all.
  // `past_product_user` (below) is the derived badge for the one value reps
  // actually act on.
  { key: 'previous_job_change_type',      label: 'Prior Engagement Type',         type: 'picklist' },
  // `previous_job_change_type = 'Product User'` — the signal Spencer/Daniel's
  // ticket (GTMOPS-2137) calls "Past Product User": this specific contact
  // used Clay's product at their PREVIOUS company. Derived, not a raw column.
  { key: 'past_product_user',             label: 'Past Product User',             type: 'boolean' },
  // Label + render both changed (2026-08-26, Spencer): "Job Changer" read as
  // an internal-jargon boolean. The underlying column/filter is unchanged —
  // still `sc.previous_job_changer` — only the display changed: when true,
  // ContactFlowTable renders a link to the prior contact record via the
  // wire-only `previous_job_contact_id` companion (same pattern as
  // `came_from`/`came_from_account_id` below) instead of a bare checkbox.
  { key: 'previous_job_changer',          label: 'Had Previous Contact',          type: 'boolean' },
  { key: 'past_employers_clay_customers', label: 'Past Employer (Clay Customer)', type: 'text' },
  { key: 'came_from',                     label: 'Came From',                     type: 'text' },
  { key: 'last_trial_start',              label: 'Last Trial Start',              type: 'date' },

  // ── Went To (2026-08-27, Spencer) — the Alumni-context mirror of Came From
  // ─────────────────────────────────────────────────────────────────────────
  // For an alumnus (`no_longer_at_company`), the successor contact record at
  // the account they moved TO: the new employer (linked, internal Terra
  // account link via `new_employer_account_id`), their new title, and the
  // successor contact record itself (linked via `new_contact_id`, the "Had
  // Previous Contact" pattern in reverse).
  //
  // Backed by the `successor` LATERAL + `new_sa` joins on the contacts query
  // (CONTACT_WENT_TO_JOIN_SQL, src/api/flow.ts) — which REPLACED the
  // dedicated `GET /api/flow/accounts/:id/went-to` endpoint (deleted
  // 2026-08-27). That endpoint's doc argued the reverse view could not be a
  // contact-view column; that is true of the FILTER system only (no filter
  // tuple over the viewed account's own contacts expresses the inversion) and
  // not of the FROM clause, where a self-join expresses it directly.
  //
  // SCOPED TO ALUMNI IN SQL, NOT BY PRESET: the join is gated on
  // `sc.no_longer_at_company IS TRUE`, so these are always blank for a
  // non-alumnus — exactly what the old `WentToBlock` rendered. They are
  // catalog-wide (any preset can add them from the Columns picker, and
  // `new_employer is_not_empty` is a useful ad-hoc filter) but front-loaded
  // ONLY by the Alumni preset — the same shape as `came_from` (catalog-wide,
  // front-loaded only by `contact-job-changer`) and `tracker_date`/
  // `tracker_note` (catalog-wide, front-loaded only by the Tracker preset).
  //
  // Their linking companions (`new_employer_account_id`, `new_contact_id`)
  // ride ContactRowSchema ONLY and are deliberately absent from this catalog
  // — same convention as `came_from_account_id`/`previous_job_contact_id`.
  { key: 'new_employer',                  label: 'New Employer',                  type: 'text' },
  { key: 'new_title',                     label: 'New Title',                     type: 'text' },
  { key: 'new_contact',                   label: 'New Contact',                   type: 'text' },

  // ── Signal columns (plan 2026-09-10-001 D3/D13) ───────────────────────────
  // The `terra_signals` view's rep-facing fields, sourced from the `sig`
  // derived join on the contacts query (src/api/flow.ts) and showable as
  // ordinary columns on EVERY contact mount — not a Plays-only overlay, which
  // is what 003's wire-only `signal_*` fields were.
  //
  // WHICH signal row they describe: the one the active signal filter pins
  // (one AND group of Signal conditions → the same predicate the filter's
  // EXISTS uses), else the contact's NEWEST signal row (D2). The values are
  // always real schema values — never a label a renderer invented.
  //
  // `signal_status_rank` is the sortable companion to `signal_status`,
  // exactly like `tracker_rank` / "Tracker Order": a status column sorts
  // alphabetically, which is meaningless for a workflow order.
  //
  // Catalogued for the FILTER picker under 'Signal' (not 'Contact') by
  // `CONTACT_SIGNAL_FILTER_FIELDS` in filter-fields.ts — same split the
  // tracker and the account cross-join fields use. The nine field defs
  // themselves live in `CONTACT_SIGNAL_FIELDS` above (spread in here) —
  // that single declaration is also `CONTACT_SIGNAL_FIELD_KEYS`' source, so
  // no key list is ever hand-written twice.
  ...CONTACT_SIGNAL_FIELDS,

  // Last Queue Send Date (D4) — the date behind the "Recent Queue Send (30d)"
  // boolean, off the same `qs` aggregate so the two can never disagree.
  // Catalogued for the filter picker under 'Terra Actions' alongside that
  // boolean, not under 'Contact'.
  { key: 'last_queue_send_date',          label: 'Last Queue Send Date',          type: 'date' },

  // Play membership columns (U13 / D4) — see `CONTACT_PLAY_FIELDS` above.
  ...CONTACT_PLAY_FIELDS,

  // ── Clay Role (Product Usage) ─────────────────────────────────────────────
  // ONE display column, THREE filter fields. The row carries `usage_roles`
  // (an ordered ContactRole[] the endpoint folds from its `is_champion` /
  // `is_key_builder` / `is_builder` SQL booleans, src/api/flow/wire-rows.ts)
  // and the column renders it as compact chips. `noFilter` because an array
  // has no operator in the filter machinery; the FILTER side is the three
  // booleans (`USAGE_ROLE_FILTER_FIELDS`, filter-fields.ts), grouped under
  // this same label in the picker so "Clay Role: Champion is true" reads as
  // one thing. `noFilter` also makes FlowTable treat it as unsortable, and
  // the endpoint has no field-map entry for it, so `sort=usage_roles` 400s:
  // an ORDER BY over three correlated EXISTS would probe every row of the
  // mirror. Real catalog column since 2026-09-25 (it was a client-only
  // synthetic column fed by the contact-state batch read before that).
  { key: USAGE_ROLE_KEY,                  label: USAGE_ROLE_LABEL,                type: 'text', noFilter: true },
];

export const CONTACT_FIELD_BY_KEY: Record<string, ContactFieldDef> =
  Object.fromEntries(CONTACT_FIELDS.map(f => [f.key, f]));

// ── Row wire schema (trust boundary for /api/flow/contacts) ─────────────────

/**
 * One contact row. Carries the join keys (`id` = SFDC contact id,
 * `account_id` = SFDC account id, `email`) so cross-app overlays and the
 * enroll queue attach later without a refactor (build-for-the-join).
 * Dates are ISO strings; scores are plain numbers.
 */
export const ContactRowSchema = z.object({
  id: z.string(),
  account_id: z.string().nullable(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  title: z.string().nullable(),
  account_name: z.string().nullable(),
  fit_score: z.number().nullable(),
  icp_fit_score: z.number().nullable(),
  persona: z.string().nullable(),
  persona_tier: z.string().nullable(),
  contact_stage: z.string().nullable(),
  total_engagement_score: z.number().nullable(),
  product_engagement_score: z.number().nullable(),
  signals_engagement_score: z.number().nullable(),
  last_workspace_login: z.string().nullable(),
  last_email_sent: z.string().nullable(),
  last_activity: z.string().nullable(),
  linkedin: z.string().nullable(),
  gong_in_flow: z.boolean().nullable(),
  gong_flow_name: z.string().nullable(),
  lim_combined: z.string().nullable(),
  lim_date: z.string().nullable(),
  lim_type: z.string().nullable(),
  lim_name: z.string().nullable(),
  lim_subtype: z.string().nullable(),
  lim_description: z.string().nullable(),
  lim_link: z.string().nullable(),
  job_start_date: z.string().nullable(),
  job_level: z.string().nullable(),
  best_phone: z.string().nullable(),
  owner_name: z.string().nullable(),
  claydr_name: z.string().nullable(),
  gs_name: z.string().nullable(),
  mailing_state: z.string().nullable(),
  mailing_city: z.string().nullable(),
  mailing_country: z.string().nullable(),
  created_date: z.string().nullable(),
  last_gtme_email: z.string().nullable(),
  last_claydr_email: z.string().nullable(),
  last_gtme_meeting: z.string().nullable(),
  last_gs_meeting: z.string().nullable(),
  last_claydr_meeting: z.string().nullable(),
  last_email_date: z.string().nullable(),
  last_inbound_form: z.string().nullable(),
  has_workspace: z.boolean().nullable(),
  gong_active_flows: z.number().nullable(),
  no_longer_at_company: z.boolean().nullable(),
  has_opted_out_of_email: z.boolean().nullable(),
  do_not_call: z.boolean().nullable(),
  follows_clay_linkedin: z.boolean().nullable(),
  suppression_reasons: z.string().nullable(),
  /** Synced Contact_Notes__c — seeds the detail's Contact Notes editor (not a table column). */
  contact_notes: z.string().nullable(),

  // ── Account columns (U5) — the `sa` LEFT JOIN's fields on the contact row ──
  // REQUIRED-nullable, never `.optional()`: the keyset cursor is built from
  // the PARSED row (`cursorFromRow`), so an optional key the schema silently
  // drops would serialize as null and page 2 of an account-sorted list would
  // restart mid-list — the exact F4 bug that kept these filter-only. Null
  // means "no account row / no value", not "not selected": the endpoint
  // SELECTs all 12 unconditionally (pinned by
  // src/api/__tests__/flow-account-columns-on-contacts.test.ts).
  //
  // The four Decimal(20,4) columns (arr_active_subscriptions, open_pipeline,
  // number_of_open_opp, number_of_employees) are `::float8`-cast in the
  // SELECT — an uncast Prisma Decimal arrives as a string and this schema
  // rejects the row (deliberately: a silently-stringified number would sort
  // lexicographically).
  account_arr: z.number().nullable(),
  account_open_pipeline: z.number().nullable(),
  account_last_opportunity_stage: z.string().nullable(),
  account_number_of_open_opp: z.number().nullable(),
  account_propensity_score: z.number().nullable(),
  account_industry: z.string().nullable(),
  account_company_segment: z.string().nullable(),
  account_sales_tier: z.string().nullable(),
  account_engagement_status_90_days: z.string().nullable(),
  account_number_of_employees: z.number().nullable(),
  account_is_agency: z.boolean().nullable(),
  account_billing_country: z.string().nullable(),
  account_billing_city: z.string().nullable(),
  // Bucket A/E follow-up (U5, 2026-08-27): populates on the first hourly
  // object-sync after deploy (Region__c ships from Middleman as of
  // terra-middleware PR #257).
  account_region: z.string().nullable(),
  /** US Fortune 500 rank of the contact's account (1 is the largest), null outside the list. */
  account_f500_rank: z.number().nullable(),
  account_owner_name: z.string().nullable(),
  account_claydr_name: z.string().nullable(),

  // ── Tracker (U7 / R6) — LEFT JOIN flow_record_trackers ────────────────────
  // Null when the record has no tracker row (Decision 9: clearing DELETEs the
  // row, so absence is the only "no status" representation).
  //
  // `tracker` is the derived bucket label ('Due' / 'Starred' / 'Reminders' /
  // 'Snoozed' / 'No status') the row groups under, and `tracker_rank` is the
  // same CASE as 0-4 — the Tracker view's auto-sort key. `tracker_rank` is on
  // the wire because keyset cursors are built from the parsed row: an ORDER BY
  // level whose alias the schema strips would round-trip as null and the next
  // page would restart mid-list (src/services/flow/query/keyset.ts).
  //
  // `.optional()`, not required-nullable: the server always SELECTs all five
  // (pinned by flow-tracker-query.test.ts), but making them required would
  // force every existing ContactRow fixture in the frontend suites to grow
  // five keys in the same wave the tracker UI is being built. Tighten to
  // required once the tracker chip unit lands.
  tracker: z.string().nullable().optional(),
  tracker_status: z.string().nullable().optional(),
  /** `YYYY-MM-DD` — a DATE column, SELECTed as text (no clock, no zone). */
  tracker_date: z.string().nullable().optional(),
  tracker_note: z.string().nullable().optional(),
  tracker_rank: z.number().nullable().optional(),

  // ── Signal columns (plan 2026-09-10-001 D2/D3/D13; 2026-09-22 added
  // signal_completed_by) ────────────────────────────────────────────────────
  // REQUIRED-nullable, not `.optional()`, and that distinction is load-bearing
  // twice over: the endpoint SELECTs all ten unconditionally (they are
  // ordinary catalog columns on every mount now, not a Plays-only overlay),
  // and the keyset cursor is built from the PARSED row — a key the schema
  // dropped would round-trip as null and page 2 would restart mid-list (the
  // F4 bug). Every `ContactRow` fixture carries them for that reason.
  //
  // Which signal row they describe is `signalScopeWhere`'s decision (D2): the
  // row the active signal filter pins when the filter is one AND group of
  // Signal conditions, else the contact's newest signal row. A contact with
  // no signal row at all keeps its row with all ten null (the join is LEFT).
  signal_status: z.string().nullable(),
  /** Campaign rows only — `true` once the member reached a responded status. Null on every other Signal Type. */
  has_responded: z.boolean().nullable(),
  signal_subtype: z.string().nullable(),
  signal_name: z.string().nullable(),
  signal_type: z.string().nullable(),
  /** `array_position(CAMPAIGN_STATUS_ORDER, signal_status)` — the sortable companion to `signal_status`. */
  signal_status_rank: z.number().int().nullable(),
  signal_action_state: z.string().nullable(),
  signal_source_system: z.string().nullable(),
  /** A9 — the Clay-pushed suggestion's classification of the person (`Prospect`, `Customer`, …). Campaign signals only; null on every other Signal Type. */
  signal_invitee_type: z.string().nullable(),
  /** 2026-09-22 "Actioned By" — who personally completed the action (invited, sent) on this signal row; null when never completed or unresolvable. */
  signal_completed_by: z.string().nullable(),
  /** ISO timestamp of the newest SUBMITTED queue send (`qs.last_send_at`, D4) — the date twin of the `has_recent_queue_send` filter. */
  last_queue_send_date: z.string().nullable(),
  // ── Play membership columns (plan 2026-09-24-005 U13 / D4) ────────────────
  // Projected ONLY when the request carries `not_in_play` (the `pm` LATERAL);
  // otherwise the keys are absent from the row, not NULL-projected, so the
  // statement without `not_in_play` stays byte-identical to before. Hence
  // `.nullable().optional()`: absent = "no play selected", null = "this
  // contact has no suggestion row for the selected play".
  /** True when the selected play has a pure-suggestion row for this contact (pinned + tagged on the "Not in this play yet" section). */
  play_suggested: z.boolean().nullable().optional(),
  /** That suggestion row's play status, verbatim (`Suggested`). */
  play_status: z.string().nullable().optional(),
  /** That suggestion row's invitee type (`Prospect`, `Customer`, …). */
  play_invitee_type: z.string().nullable().optional(),
  // Plan 2026-09-25-002 U1/D5 — the `pm` join's whole membership row, now
  // populated for all three sections (not just "Not in this play yet").
  // Same absent-vs-null rationale as the three keys above: absent = no play
  // selected, null = this contact has no membership row for the selected play.
  /** Whether this contact has responded on the selected play's membership row. */
  play_has_responded: z.boolean().nullable().optional(),
  /** Newest Terra queue send date for this contact on the selected play. */
  play_last_queue_send_at: z.string().nullable().optional(),
  /** Who sent that queue item (email). */
  play_last_queue_sent_by: z.string().nullable().optional(),
  /** Who personally actioned (invited/sent) this contact on the selected play — the Mine chip's field. */
  play_completed_by: z.string().nullable().optional(),

  // ── Job Changers / New Hires / Clay Users (U7, R1/R2) ─────────────────────
  // `.optional()`, not required-nullable, for the SAME reason as the tracker
  // block above: the endpoint always SELECTs these six unconditionally, but
  // making them required would force every existing ContactRow fixture in the
  // frontend suites (built as full literal objects, not derived from a base)
  // to grow six keys in the same wave this backend-only unit lands — those
  // fixtures are owned by U7B, not this unit. Tighten to required-nullable
  // once U7B's frontend consumption lands and updates them.
  previous_job_change_type: z.string().nullable().optional(),
  /** Derived: `previous_job_change_type = 'Product User'` — see CONTACT_FIELDS' doc comment. */
  past_product_user: z.boolean().nullable().optional(),
  previous_job_changer: z.boolean().nullable().optional(),
  past_employers_clay_customers: z.string().nullable().optional(),
  /** Previous account's name (`prev_sa.name` — see CONTACT_FIELDS' doc comment). */
  came_from: z.string().nullable().optional(),
  /** Linking companion for `came_from` — the raw previous-account SFDC id, wire-only (not a catalog/FIELD_MAP field). */
  came_from_account_id: z.string().nullable().optional(),
  /** Linking companion for `previous_job_changer`'s "Had Previous Contact" render — the prior contact record's own id, wire-only (not a catalog/FIELD_MAP field). */
  previous_job_contact_id: z.string().nullable().optional(),
  last_trial_start: z.string().nullable().optional(),

  /** Went To: successor's account NAME (`new_sa.name`) — see CONTACT_FIELDS' doc. */
  new_employer: z.string().nullable().optional(),
  /** Went To: successor's title (`successor.title`). */
  new_title: z.string().nullable().optional(),
  /** Went To: successor contact's NAME (`successor.name`). */
  new_contact: z.string().nullable().optional(),
  /** Linking companion for `new_employer` — the successor's raw account SFDC id, wire-only. */
  new_employer_account_id: z.string().nullable().optional(),
  /** Linking companion for `new_contact` — the successor contact's own SFDC id, wire-only. */
  new_contact_id: z.string().nullable().optional(),

  /**
   * Clay Role: every Product Usage role this contact holds on its account,
   * most significant first (`rolesFromFlags`, folded server-side from the
   * three `is_*` SQL booleans). `[]` when none. `.optional()` for the same
   * fixture reason as the job-changer block above; the endpoint always emits
   * it, and it is never a sort key so the keyset cursor never reads it.
   */
  usage_roles: z.array(ContactRoleSchema).optional(),
});
export type ContactRow = z.infer<typeof ContactRowSchema>;

export const ContactsResponseSchema = z.object({
  rows: z.array(ContactRowSchema),
  next_cursor: z.string().nullable(),
});
export type ContactsResponse = z.infer<typeof ContactsResponseSchema>;

// ── Reusable cross-dimension FilterGroups (contact-view scope) ──────────────
// Mirrors of account-view.ts's `CHIP_NO_OPEN_OPP` / `CHIP_NOT_AGENCY`, but
// keyed on the contact query's joined-in account rollup columns
// (`account_number_of_open_opp` / `account_is_agency`) instead of the
// accounts-table columns those use — the two catalogs use different field
// keys for the same underlying account attribute (see the `account_*`
// cross-join note above), so the account-view consts can't be reused as-is.
// Consumed by the ClayDR contact-view landing default
// (web/app-home/src/components/flow/roleDefaults.ts) for account/contact
// filter parity.

/**
 * "No open opp" (contact-view scope) — same OR-null-band rationale as
 * `CHIP_NO_OPEN_OPP`: the SFDC formula is null (not 0) when the account has
 * no open opps, so match `= 0` OR empty; a bare `= 0` would miss the null band.
 */
export const CHIP_CONTACT_ACCOUNT_NO_OPEN_OPP: FilterGroup = {
  id: 'chip-contact-no-open-opp',
  logic: 'OR',
  conditions: [
    { id: 'chip-contact-noo-1', field: 'account_number_of_open_opp', operator: 'equals', value: 0 },
    { id: 'chip-contact-noo-2', field: 'account_number_of_open_opp', operator: 'is_empty', value: null },
  ],
};

/**
 * "Not an agency" (contact-view scope) — `account_is_agency` equals false.
 * condition-to-sql's boolean handling coalesces a NULL to false generically
 * by field TYPE (not by field name), so this single condition also matches
 * contacts whose account has no agency flag set — no OR-null band needed,
 * same as `CHIP_NOT_AGENCY`.
 */
export const CHIP_CONTACT_ACCOUNT_NOT_AGENCY: FilterGroup = {
  id: 'chip-contact-not-agency',
  logic: 'AND',
  conditions: [
    // Boolean filter values travel as strings over the wire — see
    // `CHIP_NOT_AGENCY`'s comment in account-view.ts for the full rationale.
    { id: 'chip-contact-agency-1', field: 'account_is_agency', operator: 'equals', value: 'false' },
  ],
};

/**
 * "No queue send in the last 30 days" — the Plays mount's default filter row
 * (plan 2026-09-09-003 D5/D6, R3). Seeded by `buildRoleDefaultFilter` for the
 * play mount so it renders as a VISIBLE, clearable chip rather than hidden
 * scope; Reset restores it. Also carried (as its own, second filter group, so
 * it clears separately from the alumni-folded first group) by the two
 * Last Interesting Moment presets, `contact-lim` and `contact-lim-f500`
 * (2026-10-21). The group id is identical, so `withRoleDefaults` (which
 * dedupes by group id) never doubles it on the plays surface.
 *
 * `has_recent_queue_send` is a scalar-EXISTS boolean field, not an
 * `exists`-backed one, which is what makes `equals false` mean "has no
 * submitted send in the window" — see its doc comment in
 * src/shared/flow/filter-fields.ts. Boolean filter values travel as strings
 * over the wire, same as `CHIP_CONTACT_ACCOUNT_NOT_AGENCY` above.
 */
export const CHIP_NO_RECENT_QUEUE_SEND: FilterGroup = {
  id: 'chip-no-recent-queue-send',
  logic: 'AND',
  conditions: [
    { id: 'chip-no-recent-queue-send-1', field: 'has_recent_queue_send', operator: 'equals', value: 'false' },
  ],
};

/**
 * Retired contact preset ids → the preset that replaced them. A rep's saved
 * view pointer or an old `?view=` link carrying a key here resolves to the new
 * preset instead of an unknown-view error. "Product Users (L90)" merged into
 * Clay Users (2026-10-21).
 */
export const CONTACT_PRESET_ID_ALIASES: Readonly<Record<string, string>> = {
  'contact-product-users-l90': 'contact-clay-users',
};

/** Map a retired contact preset id to its replacement; anything else (including null/undefined) passes through. */
export function resolveContactPresetId<T extends string | null | undefined>(id: T): T | string {
  return (id != null && Object.prototype.hasOwnProperty.call(CONTACT_PRESET_ID_ALIASES, id)) ? CONTACT_PRESET_ID_ALIASES[id] : id;
}

// ── Seeded presets (v0 — code-defined, no DB) ────────────────────────────────

/**
 * Default column set — the FULL default-view field list from the Views doc
 * (3927), riding along in every contact view. Presets front-load their own
 * fields after Name; front-loading reorders, never removes.
 * gong_in_flow lives in CONTACT_FIELDS (filterable/re-addable via Columns) but
 * is off the default set: the ⚡ "In flow" pill by the name already carries it.
 * Persona is catalog-only per the spec.
 */
export const CONTACT_DEFAULT_COLUMNS = [
  // Identity & context (Name · Email · LinkedIn lead, per QA). Clay Role sits
  // by Title: who the person is, then what they are to Clay. (Not right after
  // Name, which the mount reserves for the injected Terra Actions strip.)
  'name', 'email', 'linkedin', 'title', USAGE_ROLE_KEY, 'account_name',
  // Scores
  'fit_score', 'icp_fit_score', 'total_engagement_score', 'product_engagement_score', 'signals_engagement_score',
  // Stage & product usage
  'contact_stage', 'has_workspace', 'last_workspace_login',
  // Engagement recency
  'last_activity', 'last_email_sent', 'last_email_date', 'last_inbound_form',
  // Last Interesting Moment
  'lim_combined', 'lim_date', 'lim_name', 'lim_type', 'lim_subtype', 'lim_description', 'lim_link',
  // Reach & coverage
  'best_phone', 'owner_name', 'claydr_name',
  'last_gtme_meeting', 'last_gtme_email', 'last_gs_meeting', 'last_claydr_meeting', 'last_claydr_email',
  'gong_active_flows',
  // Location & hygiene
  'mailing_state', 'mailing_country',
  'no_longer_at_company', 'has_opted_out_of_email', 'do_not_call', 'suppression_reasons',
  // Links
  'sfdc_link',
];

/** Column composition rule: Name (pinned) → front-loaded → remaining defaults. */
export function composeColumns(frontLoaded: string[]): string[] {
  const rest = CONTACT_DEFAULT_COLUMNS.filter(c => c !== 'name' && !frontLoaded.includes(c));
  return ['name', ...frontLoaded.filter(c => c !== 'name'), ...rest];
}

// No `scope` (U5 / R2). Presets used to default to `scope: 'my'`, an invisible
// ownership narrowing the rep could neither see nor clear. Contacts are now
// mirror-wide by default and ownership arrives — when a role wants it — as an
// ordinary VISIBLE `{me}` filter row (web/app-home/src/components/flow/
// roleDefaults.ts), which the rep can read off the Filters popover and delete.
// `groupBy` defaults to `[]` (no grouping) so every OTHER preset below can
// keep omitting it — only the Tracker preset sets a real value. Mirrors
// `normalizeGroupBy(undefined)`'s own default (view.ts), so a code-defined
// preset and a freshly parsed `FlowViewSchema` payload agree on "absent".
function preset(p: Omit<FlowView, 'createdBy' | 'entity' | 'groupBy'> & { groupBy?: string[] }): FlowView {
  return { createdBy: null, entity: 'contact', groupBy: [], ...p };
}

/**
 * Contacts who've left their company (`no_longer_at_company`) are auto-excluded
 * from every non-alumni preset and surfaced only under the "Alumni" view.
 * Boolean SQL is `COALESCE(col, false) = false`, so this keeps null rows (the
 * vast majority — the flag is set only on confirmed departures).
 */
const EXCLUDE_ALUMNI: FilterCondition = {
  id: 'x-alumni', field: 'no_longer_at_company', operator: 'equals', value: 'false',
};

/** AND the alumni-exclusion into a preset's filter (all preset groups are AND). */
function excludeAlumni(groups: FilterGroup[]): FilterGroup[] {
  if (groups.length === 0) {
    return [{ id: 'g-alumni', logic: 'AND', conditions: [{ ...EXCLUDE_ALUMNI }] }];
  }
  const [first, ...rest] = groups;
  if (first.logic === 'AND') {
    return [{ ...first, conditions: [...first.conditions, { ...EXCLUDE_ALUMNI }] }, ...rest];
  }
  // Non-AND first group: prepend the exclusion as its own AND group (groups are
  // combined with the table's AND group-logic).
  return [{ id: 'g-alumni', logic: 'AND', conditions: [{ ...EXCLUDE_ALUMNI }] }, ...groups];
}

/** The Last Interesting Moment column block shared by `contact-lim` and `contact-lim-f500`. */
const LIM_COLUMNS = [
  'email',
  // lim_link (the Signal ↗) leads the LIM block — it's the click-through
  // and must be visible without horizontal scroll (Spencer, 2026-07-24).
  'lim_link', 'lim_date', 'lim_name', 'lim_type', 'lim_subtype', 'lim_description',
  'claydr_name', 'owner_name', 'best_phone', 'last_activity',
  'last_gtme_email', 'last_claydr_email', 'last_gtme_meeting',
];

/** "Has a Last Interesting Moment" — the filter condition both LIM presets share. */
const limCondition = (id: string): FilterCondition => ({ id, field: 'lim_date', operator: 'is_not_empty', value: null });

/**
 * Playhouse column parity for the three Plays-tab People sections (Responded,
 * Not Responded, Not in this play yet): the contact/account columns Playhouse
 * shows next to a play's people, appended after each section's own triage
 * columns. ClayDR and Growth Strategist come first so they sit right after the
 * owner column. All are non-play, non-Signal contact fields. `sfdc_link` and
 * `linkedin` are display-only (`noFilter`).
 */
const PLAY_SECTION_PARITY_COLUMNS = [
  'claydr_name', 'gs_name', 'email', 'linkedin', 'persona', 'job_level', 'contact_stage', 'fit_score',
  'icp_fit_score', 'mailing_city', 'mailing_state', 'sfdc_link', 'account_arr', 'account_sales_tier',
  'has_opted_out_of_email', 'do_not_call', 'no_longer_at_company', 'suppression_reasons',
] as const;

/**
 * v0 contact presets. "Warm Intro Available" was the other spec'd preset once
 * parked here for a field that hadn't synced yet — it unblocked first (see
 * `contact-warm-intros` below). "Former Job Changers" (parked for
 * `Previous_Job_Changer__c`) unblocks in this same wave (U7, R1) now that U2
 * synced the job-changer trio — see `contact-former-job-changers` /
 * `contact-new-hires` below.
 *
 * Every preset below auto-excludes alumni; the trailing "Alumni" preset is the
 * one view that shows them (and only them).
 */
const BASE_PRESETS: FlowView[] = [
  preset({
    id: 'contact-all',
    name: 'All Contacts',
    filter: [],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns([]),
    isDefault: true,
  }),
  preset({
    id: 'contact-lim',
    name: 'Last Interesting Moment',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [limCondition('c1')],
    }, CHIP_NO_RECENT_QUEUE_SEND],
    sort: { field: 'lim_date', dir: 'DESC' },
    columns: composeColumns(LIM_COLUMNS),
    isDefault: false,
  }),
  // Contacts at EVERY ranked Fortune 500 account (subsidiaries inherit the parent's rank in
  // Salesforce) with a Last Interesting Moment, grouped by rank NUMBER only (rank 1 first; all
  // accounts sharing a rank are one group, headed "Parent rank: #N"), newest moment first inside
  // each group. Mirrors `contact-lim`'s filter.
  preset({
    id: 'contact-lim-f500',
    name: 'F500 Last Interesting Moment',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [
        { id: 'c1', field: 'account_f500_rank', operator: 'is_not_empty', value: null },
        limCondition('c2'),
      ],
    }, CHIP_NO_RECENT_QUEUE_SEND],
    sort: { field: 'lim_date', dir: 'DESC' },
    groupBy: ['account_f500_rank'],
    columns: composeColumns(['account_f500_rank', 'account_name', ...LIM_COLUMNS]),
    isDefault: false,
  }),
  // Clay Users / Non-Clay Users (2026-10-21) — "Product Users (L90)" was merged
  // into Clay Users, so the pair is exact complements over a 365-day workspace
  // login window. Clay Users sorts newest login first, which is the shape of
  // `salesforce_contact_last_workspace_login_detected_idx`
  // (`last_workspace_login_detected DESC NULLS LAST, id`). Non-Clay Users keeps
  // never-logged-in contacts (`not_in_last_days` keeps NULL rows). Alumni stay in
  // their own view. The retired L90 id lives on only as an alias
  // (`CONTACT_PRESET_ID_ALIASES`).
  preset({
    id: 'contact-clay-users',
    name: 'Clay Users',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'last_workspace_login', operator: 'in_last_days', value: 365 }],
    }],
    sort: { field: 'last_workspace_login', dir: 'DESC' },
    columns: composeColumns(['last_workspace_login', 'last_trial_start', 'has_workspace']),
    isDefault: false,
  }),
  preset({
    id: 'contact-non-clay-users',
    name: 'Non-Clay Users',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'last_workspace_login', operator: 'not_in_last_days', value: 365 }],
    }],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns([]),
    isDefault: false,
  }),
  // Newly In-Seat — reverted 2026-08-26 to its pre-GTMOPS-2137 form. A brief
  // detour: this preset was merged with the job-changer population for one
  // round (a `groupBy: 'job_change_bucket'` single view, 'New Hire' /
  // 'Newly In-Seat' sections) to satisfy Daniel Kraft's original ask —
  // separating external hires from internal promotions within recently-
  // seated contacts. That merge violated Terra's own rule that an active
  // grouping must be visible/selectable in the Group picker: `job_change_
  // bucket` worked as a derived groupBy field but was never added to
  // `groupableColumns` (FlowTable.tsx) the way `tracker` is, so the split it
  // was doing was invisible there — a real UX gap Spencer caught in QA.
  // Decision (Spencer, 2026-08-26): split back into two plain views instead
  // of fixing the picker — each view's own population is self-evident from
  // its own name and columns, no custom grouping mechanism needed at all.
  // This preset is back to being exactly what it was originally.
  // Display name renamed to "New Hires" (2026-08-28, Spencer) — the `id`
  // stays `contact-newly-in-seat` on purpose (ids are internal/never
  // rep-facing, same rule as the `contact-alumni` rename below). A
  // DIFFERENT preset, `contact-new-hires` (also displayed as "New Hires"),
  // existed earlier and was retired 2026-08-26 because its filter
  // (`previous_job_change_type = 'New Hire'`) matched a picklist value that
  // does not exist anywhere in prod — see the comment block above
  // (`687` area, "Job Changers / New Hires") for that history. Reusing the
  // display name "New Hires" here for THIS preset's population
  // (`job_start_date in_last_days 90`) is a deliberate, separate product
  // decision — different id, different filter — not a resurrection of the
  // old broken preset.
  preset({
    id: 'contact-newly-in-seat',
    name: 'New Hires',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'job_start_date', operator: 'in_last_days', value: 90 }],
    }],
    sort: { field: 'job_start_date', dir: 'DESC' },
    columns: composeColumns(['job_start_date', 'job_level']),
    isDefault: false,
  }),
  // Previous Contacts @ Co. (U7, R1 — GTMOPS-2137's job-changer trio, split
  // back out 2026-08-26 — see Newly In-Seat's comment above for the full
  // history; renamed from "Job Changer" the same day, final naming call).
  // Population is exactly `previous_job_changer = true` — the CONFIDENT
  // signal (we tracked the employer change and minted a linked contact
  // record), unrelated to recency: an old job change still belongs here.
  // Came From / Had Previous Contact resolve to real data for every row in
  // this view by construction. `id` intentionally NOT renamed alongside the
  // display name — ids are internal/never rep-facing, and nothing has saved
  // a view against this one yet regardless.
  preset({
    id: 'contact-job-changer',
    name: 'Previous Contacts @ Co.',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'previous_job_changer', operator: 'equals', value: 'true' }],
    }],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns([
      'came_from', 'previous_job_changer', 'past_product_user',
      'past_employers_clay_customers', 'job_start_date',
    ]),
    isDefault: false,
  }),
  // Past Product User (U9, R11 — GTMOPS-2137): a related population to
  // Previous Contacts @ Co. above, one specific value of the same
  // `previous_job_change_type` field (see CONTACT_FIELDS' doc comment on
  // `past_product_user`). Placed next to `contact-job-changer` on purpose.
  // "Came From a Paying Customer" (`past_employers_clay_customers
  // is_not_empty`) was explicitly deferred — not built here; see the
  // design-gap comment in CONTACT_FIELDS above.
  preset({
    id: 'contact-past-product-user',
    name: 'Past Product User',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'past_product_user', operator: 'equals', value: 'true' }],
    }],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns([
      'past_product_user', 'came_from', 'past_employers_clay_customers',
      'last_workspace_login', 'job_start_date',
    ]),
    isDefault: false,
  }),
  // Missing number (phone staging, U17, R4): contacts with no Best Phone, the
  // population "Find missing numbers" works through. Same `is_empty` operator on
  // the existing `best_phone` field every other empty-check preset uses.
  preset({
    id: 'contact-missing-number',
    name: 'Missing number',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'best_phone', operator: 'is_empty', value: null }],
    }],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns(['best_phone']),
    isDefault: false,
  }),
  // Key Builders (Clay Role, 2026-09-25): the people who drive a meaningful
  // share of their account's attributed consumption (`is_key_builder`, the
  // KEY_BUILDER_SHARE rule in src/shared/product-usage/roles.ts). Same shape
  // as `contact-past-product-user` above: one boolean equals true, engagement
  // sort, the role column front-loaded with the product-usage context.
  // Champions and `is_any_builder` are ad-hoc filters from the same "Clay Role"
  // picker group rather than further presets.
  preset({
    id: 'contact-key-builders',
    name: 'Key Builders',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'is_key_builder', operator: 'equals', value: 'true' }],
    }],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns([USAGE_ROLE_KEY, 'title', 'last_workspace_login', 'product_engagement_score']),
    isDefault: false,
  }),
  // Tracker (U7 / R6 — prototype `p-track`). No tracker FILTER: the view shows
  // the whole book bucketed by tracker state, so a starred contact and an
  // untracked one both appear (untracked ones under 'No status'). Ordering is
  // Due → Starred → Reminders → Snoozed → No status with the most overdue row
  // first — contributed server-side by the `groupBy: 'tracker'` prefix
  // (tracker_rank ASC, tracker_date ASC; see query/tracker-sql.ts). The sort
  // below is the WITHIN-bucket fallback for the dateless buckets (Starred /
  // No status), where every row ties at a null date.
  preset({
    id: 'contact-tracker',
    name: 'Tracker',
    filter: [],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    groupBy: ['tracker'],
    // U7: `tracker` dropped from columns — the row chip (header "Status")
    // already shows the bucket, so a redundant text column duplicated it.
    columns: composeColumns(['tracker_date', 'tracker_note']),
    isDefault: false,
  }),
  // Terra Actions filter (custom-views v0.1, Spencer 2026-08-12) —
  // closes the "Warm Intro Available" gap parked above (it was blocked on a
  // field sync that never landed; this uses the pill-type EXISTS bridge
  // instead of a raw SFDC column).
  //
  // v0.2 bug fix (Spencer 2026-08-13): swapped `state_pill_type in_any_of
  // ['Warm Intro']` for `warm_intro_available equals true`. The pill-type
  // field is a bare existence check with NO viewer-aware suppression
  // (deliberate — see its own doc in filter-fields.ts), so it matched a
  // contact whose warm intro was already queued or sent within the last 30
  // days: not actually available as a possible play, which defeats the
  // point of this preset. `warm_intro_available` mirrors
  // `warmIntroSource.resolve()`'s real suppression logic — see its doc in
  // filter-fields.ts / src/api/flow.ts.
  preset({
    id: 'contact-warm-intros',
    name: 'Warm Intros',
    filter: [{
      id: 'g1', logic: 'AND',
      conditions: [{ id: 'c1', field: 'warm_intro_available', operator: 'equals', value: 'true' }],
    }],
    sort: { field: 'fit_score', dir: 'DESC' },
    columns: composeColumns(['email', 'title', 'claydr_name', 'owner_name', 'best_phone', 'last_activity']),
    isDefault: false,
  }),
  // ── Plays (plan 2026-09-10-001 D10) ───────────────────────────────────────
  // The landing view of the PLAYS SURFACE — the same Contact component, one
  // `surface: 'plays'` prop away (U5/D9), not a second table. Nothing
  // mount-specific is encoded here (the F7/V3 rule): it is an ordinary preset
  // in every contact mount's switcher, and what makes it the plays surface's
  // landing view is `CONTACT_PLAYS_PRESET_ID` below, read by `useContactMount`.
  //
  // The seven front-loaded columns are the ones a rep reads while working a
  // play roster: which signal it is (`signal_name` — the human-readable
  // signal title, so a rep never has to open the row to know what fired),
  // where the person stands on the play (`signal_status`), whether they
  // replied (`has_responded`), which play it is (`signal_subtype` — the
  // campaign / flow name; the scoping condition uses the record id, but the
  // NAME is what a human reads), the invitee's role on the play
  // (`signal_invitee_type`), who personally actioned it
  // (`signal_completed_by`, 2026-09-22 — who invited/sent, not account
  // ownership), and when Terra last sent them anything
  // (`last_queue_send_date`, the date behind the default 30-day exclusion
  // chip). Visible without horizontal scroll, which is the whole reason a
  // preset front-loads instead of relying on the picker.
  //
  // No signal filter of its own: the roster scope arrives as a chip the Plays
  // list emits (`chip-play-scope`), and the L30 exclusion arrives as the
  // surface's role-independent default — both visible and removable.
  preset({
    id: 'contact-plays',
    name: 'Plays',
    filter: [],
    sort: { field: 'total_engagement_score', dir: 'DESC' },
    columns: composeColumns(['signal_name', 'signal_status', 'has_responded', 'signal_subtype', 'signal_invitee_type', 'signal_completed_by', 'last_queue_send_date']),
    isDefault: false,
  }),
  // ── Not in this play yet (plan 2026-09-24-005 U16 / D4, RV4) ──────────────
  // The landing view of the Plays tab's "Not in this play yet" section
  // (`surface: 'play-candidates'`). Same F7/V3 rule as `contact-plays`: an
  // ordinary preset in every contact switcher (decision 6); only the landing
  // choice is mount-specific (`CONTACT_PLAY_CANDIDATES_PRESET_ID` below).
  //
  // An EXACT column list rather than `composeColumns`: the seven triage
  // columns (person, title, account, where they stand on the play, their
  // invitee type, when Terra last sent them anything, owner) followed by the
  // Playhouse column parity set (`PLAY_SECTION_PARITY_COLUMNS`), with no
  // Signal column — the candidates mount neither renders nor offers one. It
  // omits `play_has_responded`, the send date and "Sent by" because a
  // suggestion has no send or response (RV4: there is no contact-grain field
  // for "Sent by"). `no_longer_at_company` reads "No" always here (alumni are
  // excluded from the section). The `play_*` columns only carry values on the
  // candidates mount (a request with `not_in_play`).
  //
  // Sort `play_suggested DESC` pins suggested people on top (decision 5); the
  // route tiebreaks a `play_suggested` sort on `name ASC`. Filters are the
  // `contact-plays` preset's minus any Signal chip — `contact-plays` has none
  // of its own, so this is `[]` + the mandatory alumni exclusion.
  preset({
    id: 'contact-play-candidates',
    name: 'Not in this play yet',
    filter: [],
    sort: { field: 'play_suggested', dir: 'DESC' },
    columns: [
      'name', 'title', 'account_name', 'play_status', 'play_invitee_type', 'last_queue_send_date', 'owner_name',
      ...PLAY_SECTION_PARITY_COLUMNS,
    ],
    isDefault: false,
  }),
  // ── Responded / Not Responded (plan 2026-09-25-002 D7) ────────────────────
  // The landing views of the Plays tab's two MEMBER sections
  // (`surface: 'play-responded'` / `'play-not-responded'`). Same F7/V3 rule
  // as the two presets above: ordinary presets in every contact switcher;
  // only the landing choice is mount-specific (`useContactMount`). Columns:
  // the nine D7 play columns followed by the Playhouse column parity set
  // (`PLAY_SECTION_PARITY_COLUMNS`; was a short triage list). Sort
  // newest Terra send first (D3 default) — a tier-1 SFDC-only member with no
  // send sorts last via `buildOrderBy`'s NULLS LAST. No section predicate in
  // `filter`: the section's universe is server-enforced and shown as a
  // pinned/locked row (D1), never a rep-editable working-state group.
  preset({
    id: 'contact-play-responded',
    name: 'Responded',
    filter: [],
    sort: { field: 'play_last_queue_send_at', dir: 'DESC' },
    columns: [
      'name', 'title', 'account_name', 'play_status', 'play_has_responded',
      'play_invitee_type', 'play_last_queue_send_at', 'play_last_queue_sent_by', 'owner_name',
      ...PLAY_SECTION_PARITY_COLUMNS,
    ],
    isDefault: false,
  }),
  preset({
    id: 'contact-play-not-responded',
    name: 'Not Responded',
    filter: [],
    sort: { field: 'play_last_queue_send_at', dir: 'DESC' },
    columns: [
      'name', 'title', 'account_name', 'play_status', 'play_has_responded',
      'play_invitee_type', 'play_last_queue_send_at', 'play_last_queue_sent_by', 'owner_name',
      ...PLAY_SECTION_PARITY_COLUMNS,
    ],
    isDefault: false,
  }),
  // Job Changers / New Hires (U7, R1 — GTMOPS-2137/2170/2198/1528/2171):
  // originally shipped here as two separate presets, `contact-former-job-
  // changers` (all-time `previous_job_changer = true`) and a broken
  // `contact-new-hires` (filtered on `previous_job_change_type = 'New Hire'`,
  // a value that does not exist anywhere in prod — live-verified 2026-08-26,
  // the field's real values are `Product User`/`Job Change`/8
  // Opportunity-champion outcomes, none of them "New Hire"). Both retired
  // 2026-08-26 per Daniel Kraft's original GTMOPS-2137 feedback (his ask was
  // never "Job Changers vs New Hires" — it was splitting Newly In-Seat into
  // external hires vs internal promotions) — folded into the revised
  // `contact-newly-in-seat` preset above instead of living as their own
  // presets. `previous_job_changer = true` is still exactly the population
  // that preset's 'New Hire' group bucket uses.
];

/**
 * The alumni-only view — the sole preset that shows no_longer_at_company rows,
 * and (2026-08-27, Spencer) the one preset that front-loads the Went To trio
 * (New Employer / New Title / New Contact) — see CONTACT_FIELDS' Went To
 * block for why they're catalog-wide but front-loaded here only.
 */
const ALUMNI_PRESET: FlowView = preset({
  id: 'contact-alumni',
  // Display name renamed to "No Longer @ Co." (2026-08-28, Spencer) — `id`
  // intentionally NOT renamed alongside it, same precedent as
  // `contact-job-changer`'s rename to "Previous Contacts @ Co." above (ids
  // are internal/never rep-facing).
  name: 'No Longer @ Co.',
  filter: [{
    id: 'g1', logic: 'AND',
    conditions: [{ id: 'c1', field: 'no_longer_at_company', operator: 'equals', value: 'true' }],
  }],
  sort: { field: 'total_engagement_score', dir: 'DESC' },
  columns: composeColumns([
    // Went To (2026-08-27, Spencer): the three successor columns sit directly
    // after "No Longer at Co." — the alumni flag, then where they went. Only
    // this preset front-loads them; they stay addable from the Columns picker
    // on any view (see CONTACT_FIELDS' Went To block), and are blank for a
    // non-alumnus by construction (the join's own alumni gate), so no other
    // preset needs to exclude them.
    'no_longer_at_company', 'new_employer', 'new_title', 'new_contact',
    'title', 'account_name', 'last_activity', 'suppression_reasons',
  ]),
  isDefault: false,
});

export const CONTACT_PRESETS: FlowView[] = [
  ...BASE_PRESETS.map(p => ({ ...p, filter: excludeAlumni(p.filter) })),
  ALUMNI_PRESET,
];

export const CONTACT_DEFAULT_PRESET = CONTACT_PRESETS.find(p => p.isDefault)!;

/**
 * Landing preset for the GLOBAL Command Center contacts mount (QA-2 / V6,
 * Spencer: "reps who haven't set a home view should land on the Tracker
 * view"). Deliberately a SEPARATE id from `CONTACT_DEFAULT_PRESET.id`
 * ('contact-all', `isDefault: true`) rather than an `isDefault` flip — the
 * investigation found THREE consumers keyed off `isDefault`/`defaultPresetId`
 * that would break under a flip: `withAccountDefaultSort` (removed below —
 * see the account-mount sort note), `shouldPartitionByState`
 * (ContactFlowTable.tsx, the drill-down's Terra-Notes-first ranking), and the
 * fact that `defaultPresetId` is shared by BOTH the global mount and the
 * account-scoped drill-down (which must keep landing on `'contact-all'`, not
 * Tracker). `isDefault` therefore stays put — it means "this mount's
 * canonical baseline" and those consumers rely on that meaning — while
 * `defaultPresetId` (fed only to the GLOBAL mount) picks the LANDING view.
 * Landing precedence is otherwise unchanged: `?view=` → stored active →
 * stored default pointer → this code-defined landing constant.
 */
export const CONTACT_LANDING_PRESET_ID = 'contact-tracker';

/**
 * Landing preset for the PLAYS surface (`surface: 'plays'`, D9/D10) — the
 * same precedence the global mount's `CONTACT_LANDING_PRESET_ID` above has,
 * pointed at the Plays preset instead of Tracker. A separate constant rather
 * than an `isDefault` flip for the identical reason spelled out above: three
 * consumers key off `isDefault`/`defaultPresetId`, and the account drill-down
 * must keep landing on `contact-all`.
 */
export const CONTACT_PLAYS_PRESET_ID = 'contact-plays';

/**
 * Landing preset for the "Not in this play yet" section (`surface:
 * 'play-candidates'`, plan 2026-09-24-005 U16) — same precedence and the same
 * no-`isDefault`-flip rationale as `CONTACT_PLAYS_PRESET_ID` above.
 */
export const CONTACT_PLAY_CANDIDATES_PRESET_ID = 'contact-play-candidates';

/**
 * Landing presets for the two MEMBER sections (`surface: 'play-responded'` /
 * `'play-not-responded'`, plan 2026-09-25-002 D7) — same precedence and the
 * same no-`isDefault`-flip rationale as `CONTACT_PLAY_CANDIDATES_PRESET_ID`
 * above.
 */
export const CONTACT_PLAY_RESPONDED_PRESET_ID = 'contact-play-responded';
export const CONTACT_PLAY_NOT_RESPONDED_PRESET_ID = 'contact-play-not-responded';

/**
 * What the `contact-play-candidates` preset's `play_suggested DESC` sort
 * degrades to OFF the candidates mount (no `not_in_play` on the request —
 * plan 2026-09-25-001 U2). The preset reads "suggested first, then
 * alphabetical"; off the mount there is no "suggested", so the alphabetical
 * half is what remains — the route's own tiebreak for a `play_suggested`
 * sort. `ContactFlowTable` applies it as a render/query-time override only;
 * the view definition keeps its real sort.
 */
export const CONTACT_PLAY_CANDIDATES_OFF_MOUNT_SORT: FlowSort = { field: 'name', dir: 'ASC' };

// ── Account-scoped drill-down default ranking (Spencer, 2026-07-28) ─────────

/**
 * Account-drilldown default sort — Fit Score descending, NOT the global
 * Command Center default (`total_engagement_score` DESC, CONTACT_DEFAULT_PRESET
 * above). `ContactFlowTable` (account mount ONLY — `accountId` set) also
 * applies a client-side stable partition on TOP of this server order:
 * contacts with ≥1 "Terra Actions" pill (the contact-state read-model strip)
 * float above contacts with none, preserving this fit-score order within
 * each partition (see web/app-home/.../contact/rankByState.ts). Since the
 * server already sorts fit_score DESC, that stable partition is exactly
 * "has notes first, fit score desc within each" — no extra comparator
 * needed client-side.
 *
 * QA-2 (V3): this used to be folded INTO the default preset's stored
 * definition via a `withAccountDefaultSort(CONTACT_PRESETS)` helper applied
 * to the account mount's `views` — which meant a "Save as new view" from a
 * drill-down baked `fit_score DESC` into the saved row for EVERYONE (spec
 * forbids mount context in the model — F7 is fixed-columns-only). That
 * helper is gone; `ContactFlowTable` now applies this constant as a
 * RENDER/QUERY-TIME override (feeding `useFlowContacts` + the header sort
 * indicator) only while the account mount is on the default preset's own,
 * UNMODIFIED sort — never by mutating `working`/the view definition, so
 * Save/Save-as-new always persists the view's real (global) sort.
 */
export const CONTACT_ACCOUNT_DEFAULT_SORT: FlowSort = { field: 'fit_score', dir: 'DESC' };
